/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  CSSProperties,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import InView from 'react-intersection-observer';
import {Input, InputLabel, MenuItem, Select} from '@material-ui/core';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import {InputProps as InputPropsType} from '@material-ui/core/Input';
import {makeStyles} from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import classnames from 'classnames';
import debounce from 'lodash.debounce';
import isArray from 'lodash.isarray';
import isObject from 'lodash.isobject';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import CheckBox from '@ion/dls/src/components/checkbox/CheckBox';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import {TopNavSearchIcon} from '../../icons/TopNavSearchIcon';
import {
  dlsAlt,
  dlsColor,
  dlsTypography,
  dlsZIndex
} from '../../theme/theme';
import TextField from '../textfield/TextField';
import styles from './Dropdown.module.scss';

// defines the prop definitions for the dropdown option
export interface Options {
  label: string;
  value: any;
  disabled?: boolean;
  id?: number;
  required?: boolean;
};

// defines the prop definitions for the dropdown option
export interface NoneOptions {
  options: Options[];
  includeNone?: boolean;
  value?: any;
  label?: string;
};

export interface DropDownDefaultLevelProps extends Pick<DropDownDefaultProps, 'isChipClickable'> {
};

export interface DropDownDefaultProps {
  onChipClick?: (value: string | string[], isDeviceChip: boolean, chipName: string) => void;
  isChipDevice?: boolean;
  isChipClickable?: boolean;
};

export interface DropDownInfiniteScrollProps {
  onInfiniteScroll?: (scrollHeight: number, scrollTop: number, clientHeight: number) => void;
  isInfiniteScroll?: boolean;
  isOnSearchAssociations?: boolean;
  onSearchAssociations?: (value: string) => void;
  onHandleClickOutside?: () => void;
};

interface StyleProps {
  transparentBackground?: boolean;
};

// defines the prop definitions for the dropdown
export interface DropdownProps extends DropDownDefaultProps, DropDownInfiniteScrollProps, StyleProps {
  autoComplete?: boolean;
  required?: boolean;
  options: Options[];
  placeholder?: string;
  defaultOptionIndex?: number;
  id?: string;
  inputLabel?: string;
  multiSelect?: boolean;
  selectedVal?: string;
  upperCase?: boolean;
  displaySelectedLabel?: boolean;
  label?: string;
  fieldLabel?: string | ReactNode;
  noOptionsText?: ReactNode;
  value?: string | string[];
  openOnMount?: boolean;
  onDeleteVal?: (val: string, id: number) => void;
  onChange?: Function;
  hasError?: boolean;
  errorText?: string;
  helperText?: string;
  preferPlaceholder?: boolean;
  includeNone?: boolean;
  noneOptionValue?: any;
  noneOptionLabel?: string;
  defaultValue?: any;
  name?: string;
  useDisabledStyles?: boolean;
  disabled?: boolean;
  limitTags?: number;
  loading?: boolean;
  enableClear?: boolean;
  labelAsValue?: boolean;
  hasCTALink?: boolean;
  hasClearAll?: boolean;
  ctaLinkText?: string;
  onCTALinkClick?: Function;
  onFieldChange?: Function;
  includeSearchIcon?: boolean;
  style?: CSSProperties;
  renderOption?: (option: any) => JSX.Element;
  externalClasses?: GenericObject;
  includeSelectAll?: boolean;
  onSelectAll?: (e) => void;
  onClearAll?: (e) => void;
  additonalLabelComponent?: ReactNode;
};

// defines the param definitions for CreateOptions method ...
export type CreateOptionsArgs = {
  items: unknown[];
  valProp?: string;
  labelProp?: string;
  augmentor?: (arg: unknown, arg2: unknown) => void;
};

// defines the param definitions for CreateMultiOptions method ...
export type CreateMultiOptionsArgs = {
  items: unknown[];
  valProp?: string;
  labelProp1?: string;
  labelProp2?: string;
  augmentor?: (arg: unknown, arg2: unknown) => void;
};

/**
 * Provides a utility for creating Dropdown options from an array of items. The valProp
 * and labelProp arguments can be used to specify which properties should be used for
 * the option values and labels. In addition, an option augmentor function can be
 * provided to further enhance the returned options.
 *
 * ```typescript
 *
 *   import {createOptions, Options} from '@ion/dls/src/components/menus';
 *
 *   // extended Options ...
 *   interface CustomOption extends Options {
 *     disabled: boolean;
 *   };
 *
 *   // augmentation / customization ...
 *   const options: Options[] = createOptions({
 *     items: assets
 *     valProp: 'id',
 *     labelProp: 'name'
 *     augmentor: (item: Asset) => {
 *       return {...item, disabled: item.status !== EntityStatus.Enabled};
 *     }
 *   });
 *
 */
export const createOptions = (
  {items, valProp, labelProp, augmentor}: CreateOptionsArgs
): Options[] => {
  const options = isArray(items) ? items?.map((item: unknown) => {
    const value = valProp ? item[valProp] : item;
    const label = labelProp ? item[labelProp] : value;
    const option = {value, label};
    return augmentor ? augmentor(item, option) : option;
  }) as Options[] || [] : [];
  return options.filter((option) => option.label);
};

export const createMultiOptions = (
  {items, valProp, labelProp1, labelProp2, augmentor}: CreateMultiOptionsArgs
): Options[] => {
  return items?.map((item: unknown) => {
    const value = valProp ? item[valProp] : null;
    const label = labelProp1 && labelProp2 ? `${item[labelProp1]} ${item[labelProp2]}`
      : labelProp1 || labelProp2 || '';
    const option = {value, label};
    return augmentor ? augmentor(item, option) : option;
  }) as Options[] || [];
};

/**
 * Adds an optional "None" option to the beginning of the list so as to allow users
 * to deselect a value from the list.
 *
 * @param options     The list of options to be displayed.
 * @param includeNone If true, the none option will be added if it does not exist.
 * @param value       An optional value to use for the none option (defaults to undefined)
 */
export const noneOption = ({
  options,
  includeNone,
  value,
  label
}: NoneOptions): Options[] => {
  if (options?.length && includeNone) {
    const optionLabel = label || translate('common:options.none');
    if (options[0].label !== optionLabel) {
      options.unshift({label: optionLabel, value});
    }
  }
  return options;
};

type BackgroundStyles = {
  disabled?: boolean,
  useDisabledStyles?: boolean
};

const toggleWrapperBackground = ({transparentBackground}: StyleProps) => (
  transparentBackground ? 'transparent': dlsColor('white')
);

// defines the styles definition for the Dropdown Component.
export const useStyles = makeStyles(theme => {
  const getBackgroundColor = (isRoot: boolean) => {
    return ({disabled, useDisabledStyles}: BackgroundStyles, isRoot: boolean) => {
      if (disabled && useDisabledStyles) {
        return dlsAlt('components', 'dropdown', 'disabledBackground');
      }
      return isRoot ? 'transparent' : 'inherit';
    };
  };
  return {
    paper : {
      borderRadius : '0px',
      marginTop    : '4px',
      padding      : '0px',
      overflowY    : 'auto',
      textOverflow : 'ellipsis'
    },
    formControl : {
      width        : '100%',
      borderBottom : ({hasError}: { hasError: boolean; }) => (
        hasError ? 'none' : `1px solid ${dlsColor('grey5')}`
      ),
      '& div[class*="MuiInput-root"]' : {
        backgroundColor : getBackgroundColor(true),
        width           : '100%',
        marginTop       : 0,
        borderBottom    : ({hasError}: { hasError: boolean; }) => (
          hasError ? `solid 2px ${dlsColor('caRed')}` : 'none'
        )
      }
    },
    errorText : {
      color : `${dlsColor('caRed')}`
    },
    list : {
      paddingBottom : '0px'
    },
    select : {
      backgroundColor : getBackgroundColor(false),
      padding         : '12px 24px 11px 7px !important',
      '&:focus'       : {
        backgroundColor : 'transparent'
      }
    },
    inputLabel : {
      ...dlsAlt('components', 'dropdown'),
      color         : dlsColor('grey3'),
      textTransform : ({upperCase}: { upperCase: boolean }) => (
        upperCase ? 'uppercase' : 'none'
      ),
      letterSpacing : ({upperCase}: { upperCase: boolean }) => (
        upperCase ? dlsAlt('components', 'dropdown').letterSpacing : 'normal'
      )
    },
    label : {
      ...dlsAlt('components', 'dropdown'),
      color         : ({inputLabel}: { inputLabel: string; }) => inputLabel ? dlsColor('black') : dlsColor('grey3'),
      marginTop     : '2px',
      textTransform : ({upperCase}: { upperCase: boolean }) => (
        upperCase ? dlsAlt('components', 'dropdown').textTransform : 'none'
      ),
      letterSpacing : ({upperCase}: { upperCase: boolean }) => (
        upperCase ? dlsAlt('components', 'dropdown').letterSpacing : 'normal'
      )
    },
    root : {
      padding   : '17px !important',
      '&:hover' : {
        backgroundColor : dlsAlt('components', 'icon', 'selected')
      }
    },
    selectWrapper : {
      backgroundColor : toggleWrapperBackground
    },
    selected : {
      backgroundColor : `${dlsAlt('components', 'icon', 'selected')} !important`
    },
    popper : {
      zIndex : dlsZIndex('dropdownPopper')
    },
    inputRoot : {
      '&::before' : {
        borderBottom : 'none !important'
      },
      '&.Mui-focused' : {
        '&::before' : {
          borderBottom : `1px solid ${dlsColor('aaBlue')} !important`
        }
      }
    },
    popupIndicator : {
      '&:hover, &:active' : {
        backgroundColor : 'transparent'
      },
      '& .MuiTouchRipple-root' : {
        display : 'none'
      }
    },
    clearIndicator : {
      '&:hover, &:active' : {
        backgroundColor : 'transparent'
      },
      '& .MuiTouchRipple-root' : {
        display : 'none'
      },
      '& .MuiSvgIcon-root' : {
        width     : '0.65em',
        height    : '0.65em',
        '&:hover' : {
          color : dlsColor('caRed')
        }
      }
    },
    option : {
      padding       : '17px !important',
      ...dlsAlt('components', 'dropdown'),
      color         : dlsColor('grey3'),
      textTransform : ({upperCase}: {upperCase: boolean}) => (
        upperCase ? 'uppercase' : 'none'
      ),
      letterSpacing : ({upperCase}: {upperCase: boolean}) => (
        upperCase ? dlsAlt('components', 'dropdown').letterSpacing : 'normal'
      ),
      '&[data-focus="true"]' : {
        backgroundColor : `${dlsAlt('components', 'icon', 'selected')} !important`
      }
    },
    endAdornment : {
      paddingRight : '7px !important'
    },
    listbox : {
      padding : '0px !important',
      margin  : '0px !important'
    },
    input : {
      paddingLeft  : '7px !important',
      paddingRight : '3em !important',
      maxWidth     : ({autoComplete}: { autoComplete: boolean; }) => autoComplete ? '90%' : '100%'
    },
    placeholder : {
      top             : ({fieldLabel}: { fieldLabel: string; }) => fieldLabel ? '20px' : '-6px',
      paddingLeft     : '11px !important',
      pointerEvents   : 'none',
      zIndex          : dlsZIndex('dropdownPlaceholder'),
      ...dlsTypography('body1'),
      color           : dlsColor('grey4'),
      '&.Mui-focused' : {
        color : dlsColor('grey4')
      }
    },
    fieldLabel : {
      color        : dlsColor('grey2'),
      marginBottom : '8px',
      ...dlsTypography('body3')
    },
    requiredAsterisk : {
      color  : dlsColor('aaBlue'),
      margin : '0 2px'
    },
    flexContainer : {
      display        : 'flex',
      justifyContent : 'space-between',
      alignItems     : 'flex-start'
    },
    ctaLinkStyle : {
      height   : '20px',
      fontSize : '12px !important'
    },
    searchIcon : {
      padding        : '12px 0 12px 12px',
      display        : 'flex',
      justifyContent : 'center',
      alignItems     : 'center'
    },
    inputUnderline : {
      '&:after' : {
        borderBottom : `solid 2px ${dlsColor('caBlue')}`
      }
    }
  } as any;
});

/**
 * @Dropdown
 *
 * Defines the Dropdown component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 *
 *   import Dropdown from '@ion/dls/src/components/menus/Dropdown';
 *
 *   const defaultOption                 = options[defaultOptionIndex] || options[0];
 *   const defaultValue                  = multiSelect
 *                                         ? [defaultOption.value] : defaultOption.value;
 *   const [selectedVal, setSelectedVal] = useState(defaultValue);
 *
 *   function handleChange({target: {value}}) {
 *     setSelectedVal(target.value);
 *   };
 *
 *   <Dropdown options={[]}
 *     fieldLabel='Options'
 *     placeholder='Select an Option'
 *     value={selectedVal}
 *     onChange={setSelectedVal} /> [Placeholder + Field Label]
 *
 *   <Dropdown options={[]}
 *     placeholder='Select an Option'
 *     defaultOptionIndex={}
 *     value={selectedVal}
 *     onChange={setSelectedVal} /> [Default Option]
 *
 *   <Dropdown options={[]}
 *     placeholder='Select an Option'
 *     defaultOptionIndex={}
 *     multiSelect
 *     value={selectedVal}
 *     onChange={setSelectedVal} /> [Multiple Selection]
 *
 * ```
 */
export const Dropdown = ({
  options,
  defaultValue,
  displaySelectedLabel,
  placeholder,
  upperCase,
  onChange,
  preferPlaceholder,
  value,
  required,
  hasError,
  errorText,
  helperText,
  includeNone,
  noneOptionValue,
  noneOptionLabel,
  name,
  noOptionsText,
  disabled,
  limitTags,
  onCTALinkClick,
  onFieldChange,
  onChipClick,
  onInfiniteScroll,
  externalClasses,
  onSelectAll,
  onClearAll,
  onSearchAssociations,
  onHandleClickOutside,
  additonalLabelComponent,
  renderOption,
  transparentBackground = false,
  autoComplete = true,
  loading = false,
  enableClear = false,
  fieldLabel = '',
  id = 'AutoComplete',
  inputLabel = '',
  openOnMount = false,
  multiSelect = false,
  useDisabledStyles = false,
  labelAsValue = true,
  hasCTALink = false,
  hasClearAll = false,
  ctaLinkText = null,
  includeSearchIcon = false,
  isChipDevice = false,
  isChipClickable = false,
  isInfiniteScroll = false,
  includeSelectAll = false,
  isOnSearchAssociations = false,
  ...props
}: DropdownProps) => {
  const classes: GenericObject = useStyles({
    useDisabledStyles,
    upperCase,
    inputLabel,
    fieldLabel,
    hasError,
    disabled,
    autoComplete,
    transparentBackground
  });
  const [isAutocompleteMenuOpen, setIsAutoCompleteMenuOpen] = useState<boolean>(openOnMount);

  const [selectionMade, setSelectionMade]   = useState<boolean>(false);
  const [isNoneSelected, setIsNoneSelected] = useState<boolean>(false);
  const childRef                            = useRef(null);
  const inputKey                            = inputLabel ? inputLabel.toLowerCase() : '';
  const isAllSelected                       = value?.length === options?.length;

  useEffect(() => {
    if (!isAutocompleteMenuOpen && !!options.length && !!onFieldChange) {
      onFieldChange(null);
    }
  }, [isAutocompleteMenuOpen]);

  /**
   * @private
   */
  function showPlaceholder() {
    if (value !== translate('common:states.loading')) {
      const prioritize = preferPlaceholder || isNoneSelected;
      return prioritize || (placeholder && !selectionMade && !value?.length);
    }
    return false;
  }

  /**
   * @private
   */
  const renderSelected = (multiSelect) => (selected) => {
    // [DLS TODO]: Temporarily omitting false positive eslint error ...
    // eslint-disable-next-line react/prop-types
    const label          = options?.filter(option => option?.value === selected)[0]?.label || selected;
    const selectedOption = displaySelectedLabel ? selected : label;
    const val            = multiSelect ? selected.join(', ') : label || selectedOption;
    return (
      <div className={styles.selectWrapper}>
        {inputLabel && <span className={classes.inputLabel}>{`${inputLabel}:`}&nbsp;</span>}
        {val !== translate('common:options.none') && <span>{val}</span>}
      </div>
    );
  };

  /**
   * @private
   */
  function handleSelectChange(e: ChangeEvent<{ name?: string; value: unknown; }>, child: ReactNode) {
    setSelectionMade(true);
    setIsNoneSelected(e.target.value === translate('common:options.none'));
    onChange?.(e, child);
  }

  /**
   * @private
   */
  function handleAutoSelectChange(e, child) {
    if (onChange && e) {
      const {value, label} = child || {};
      let val;
      if (labelAsValue) {
        val = label ?? child;
      } else if (!isObject(child)) {
        val = child;
      } else {
        val = value;
      }
      onChange({
        ...e,
        target : {
          ...e.target,
          value : val,
          name  : name || inputKey,
          label : value || label,
          required
        }
      }, child);
    }
    setIsAutoCompleteMenuOpen(false);
  }

  /**
   * @private
   */
  const getOptionSelected = function({value, label}, selectedValue) {
    const selectedVal = selectedValue?.value ?? selectedValue;
    const selectedId  = selectedValue?.id;
    return (value === selectedVal ||value === selectedId ||
    label === selectedVal ||label === selectedId);
  };

  /**
   * @private
   */
  const onLinkClick = function() {
    onCTALinkClick();
  };

  const onClearAllClick = function() {
    onChange({}, []);
  };

  /**
   * @private
   */
  const getOptionLabel = function(option: any) {
    return option?.label ?? (
      options?.find(({value, label}) => label === option || value === option)?.label || option
    );
  };

  /**
   * Collapses Autocomplete's dropdown menu when the user scrolls the field out of view.
   */
  const handleCollapsingAutocompleteMenu = useCallback((isVisible: boolean): void => {
    if (isAutocompleteMenuOpen && isVisible === false) {
      setIsAutoCompleteMenuOpen(false);
    }
  }, [isAutocompleteMenuOpen]);

  /**
   * Toggles the Autocomplete's dropdown menu open (true) or closed (false);
   */
  const toggleOpen = useCallback(function(isOpen: boolean = true): (event: ChangeEvent) => void {
    return function() {
      setIsAutoCompleteMenuOpen(isOpen);
    };
  }, [isAutocompleteMenuOpen]);

  // debounces textfield changes so as to only update state once the change has been completed ...
  const debounceFieldChange = debounce((value) => {
    onFieldChange(value);
  }, 750);

  // invoked when a textfield change is made, delegating the debounce change handler ...
  const handleFieldChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    if (e && e.target.value !== translate('common:states.loading') && !!onFieldChange) {
      e.persist();
      const {value} = e.target as GenericObject;
      value !== 0 && debounceFieldChange(value);
    }
  }, []);

  // close the menu when the user intract outside of dropdown menu...
  const handleClickOutside = useCallback((e) => {
    if (!childRef?.current.contains(e.target)) {
      setIsAutoCompleteMenuOpen(false);
    }
  }, []);

  //Invoked when the includeSearchIcon is true
  const withIncludeSearchIconInput = useCallback(function(props: {InputProps: InputPropsType}) {
    props.InputProps.startAdornment = (
      <div className={classes.searchIcon}>
        <TopNavSearchIcon />
      </div>
    );
    props.InputProps.classes = {
      underline : classes.inputUnderline
    };
    props.InputProps.disableUnderline = true;
    return (
      <TextField classes={{
        root : classes.autocompleteTextFieldRoot
      }} {...props} />
    );
  }, []);

  //Invoked when the includeSearchIcon is false and it will be the default input
  const defaultInputs = useCallback((params) => (
    <FormTextField {...params}
      placeholder={loading ? translate('common:states.loading') : (placeholder ?? inputLabel)}
      required={required}
      name={name}
      onChange={handleAssociations}
      onBlur={handleOutsideClick}
      autoComplete='off'
      InputProps={{
        name : name || inputKey,
        required,
        ...params.InputProps
      }}/>
  ), [loading, placeholder, inputLabel, name, required, inputKey]);

  const onClickChip = useCallback(({target: {innerText}}) => {
    onChipClick(value, isChipDevice, innerText);
  }, [value, isChipDevice]);

  const onScroll = useCallback(({currentTarget: {scrollHeight, scrollTop, clientHeight}}) => {
    onInfiniteScroll(scrollHeight, scrollTop, clientHeight);
  }, [isInfiniteScroll]);

  const handleAssociations = useCallback(({target}) => {
    if (isInfiniteScroll || isOnSearchAssociations) {
      onSearchAssociations?.(target?.value);
    }
  }, [isInfiniteScroll]);

  const handleOutsideClick = useCallback(() => {
    if (isInfiniteScroll) {
      onHandleClickOutside();
    }
  }, [isInfiniteScroll]);

  return (
    <ClickAwayListener onClickAway={handleClickOutside}>
      <FormControl className={classes.formControl}>
        {fieldLabel && (
          <div className={classes.flexContainer}>
            <div className={classes.fieldLabel}>
              {fieldLabel}
              {required && <span className={classes.requiredAsterisk}>*</span>}
            </div>
            <div>
              {hasClearAll && (
                <TertiaryButton className={classes.ctaLinkStyle} onClick={onClearAllClick}>
                  {translate('common:editors.shared.clearAll')}
                </TertiaryButton>
              )}
              {hasCTALink && (
                <TertiaryButton className={classes.ctaLinkStyle} onClick={onLinkClick}>
                  {ctaLinkText}
                </TertiaryButton>
              )}
              {includeSelectAll && !disabled && (
                <TertiaryButton className={classes.ctaLinkStyle}
                  onClick={isAllSelected ? onClearAll : onSelectAll}>
                  {translate(`common:editors.shared.${isAllSelected ? 'clearAll' : 'selectAll'}`)}
                </TertiaryButton>
              )}
              {additonalLabelComponent && (
                <div>{additonalLabelComponent}</div>
              )}
            </div>
          </div>
        )}
        <div className={classes.selectWrapper} ref={childRef}>
          {autoComplete ? (
            <InView onChange={handleCollapsingAutocompleteMenu}>
              <Autocomplete id={id}
                classes={{
                  popper         : classes.popper,
                  paper          : classes.paper,
                  popupIndicator : classes.popupIndicator,
                  clearIndicator : classes.clearIndicator,
                  endAdornment   : classes.endAdornment,
                  input          : classnames(classes.input, externalClasses?.input),
                  inputRoot      : classes.inputRoot,
                  listbox        : classes.listbox,
                  option         : classnames(classes.option, externalClasses?.option)
                }}
                ChipProps={isChipClickable && {
                  onClick : onClickChip
                }}
                ListboxProps={isInfiniteScroll && {
                  role     : 'listbox',
                  onScroll : onScroll
                }}
                limitTags={limitTags}
                disabled={disabled}
                multiple={!loading && multiSelect}
                value={value}
                open={isAutocompleteMenuOpen}
                onOpen={toggleOpen()}
                onClose={toggleOpen(false)}
                openText=''
                closeText=''
                defaultValue={defaultValue}
                autoComplete={false}
                loading={loading}
                disableClearable={!enableClear}
                clearOnEscape
                clearText={translate('common:editors.shared.clearText')}
                loadingText={translate('common:states.loading')}
                noOptionsText={noOptionsText ?? translate('common:editors.shared.noOptionsText')}
                onChange={handleAutoSelectChange}
                onInputChange={handleFieldChange}
                getOptionSelected={getOptionSelected}
                getOptionLabel={getOptionLabel}
                options={noneOption({
                  options,
                  includeNone,
                  value : noneOptionValue,
                  label : noneOptionLabel
                })}
                getOptionDisabled={function(option: any) {
                  return option.disabled;
                }}
                renderOption={renderOption}
                renderInput={includeSearchIcon ? withIncludeSearchIconInput : defaultInputs}
                {...props} />
            </InView>
          ) : (
            <>
              {showPlaceholder() && (
                <InputLabel className={classes.placeholder}
                  disableAnimation
                  shrink={false}>
                  {placeholder}
                  {required && !fieldLabel && <span className={classes.requiredAsterisk}>*</span>}
                </InputLabel>
              )}
              <Select classes={{select: classes.select}}
                disabled={disabled}
                error={hasError}
                input={<Input />}
                inputProps={{
                  name : name || inputKey,
                  required
                }}
                onChange={handleSelectChange}
                MenuProps={{
                  anchorOrigin : {
                    vertical   : 'bottom',
                    horizontal : -4
                  },
                  transformOrigin : {
                    vertical   : 'top',
                    horizontal : 'left'
                  },
                  getContentAnchorEl : null,
                  classes            : {
                    paper : classes.paper,
                    list  : classes.list
                  }
                }}
                className={styles.select}
                id={id}
                multiple={multiSelect}
                renderValue={renderSelected(multiSelect)}
                value={value}
                {...props}>
                {noneOption({
                  options,
                  includeNone,
                  value : noneOptionValue,
                  label : noneOptionLabel
                }).map((
                  {value: optionVal, label, disabled}, index: number
                ) => (
                  <MenuItem key={`option-${optionVal}-${index}`}
                    disabled={disabled}
                    classes={{
                      root     : classes.root,
                      selected : classes.selected
                    }}
                    value={displaySelectedLabel ? label : optionVal}>
                    <div className={classes.option}>
                      {multiSelect && (
                        <CheckBox checked={value.includes(displaySelectedLabel ? label : optionVal)} />
                      )}
                      <span className={classes.label}>{label}</span>
                    </div>
                  </MenuItem>
                ))}
              </Select>
            </>
          )}
        </div>
        {(helperText || (hasError && !!errorText)) && (
          <FormHelperText className={hasError ? classes.errorText : null}>
            {hasError ? errorText : helperText}
          </FormHelperText>
        )}
      </FormControl>
    </ClickAwayListener>
  );
};

export default Dropdown;
